import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
// Text layer → selection + browser Ctrl-F search + copy; annotation layer →
// clickable links / form-field widgets. Both need react-pdf's CSS to position
// their overlays over the page canvas. CRA's jest stubs .css imports, so this
// stays test-safe. Positioning uses inline styles, which the CSP style-src
// 'unsafe-inline' already permits.
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { DownloadSimpleIcon, XIcon } from '@phosphor-icons/react';
import { GreenGradientButton } from '../../../../../shared/Buttons';
import {
  PM_CHECKLIST_DOWNLOAD_FILENAME,
  PM_CHECKLIST_PDF_URL,
} from '../../../../../constants/documents';

// Render the PDF in-app with pdf.js (react-pdf) instead of a cross-origin
// <iframe>, which the app's CSP `frame-src` blocks (THT-696). The worker is
// self-hosted from /public (copied there by the `copy-pdf-worker` postinstall
// script — keep WORKER_FILENAME in sync with it) so it loads from 'self' — the
// only origin the CSP `worker-src` allows. A plain string path is used
// deliberately: `import.meta.url` breaks CRA's jest transform. The path is
// prefixed with PUBLIC_URL so it still resolves if the app is served from a
// sub-path (homepage/PUBLIC_URL); it is '' for the current root deploy.
const WORKER_FILENAME = 'pdf.worker.min.mjs';
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/${WORKER_FILENAME}`;

const MAX_PAGE_WIDTH = 1000;
const HORIZONTAL_GUTTER = 32;

interface PMChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function PMChecklistModal({ isOpen, onClose }: PMChecklistModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);
  const [hasError, setHasError] = useState(false);

  // Reset load state each time the modal opens so a prior error doesn't stick,
  // and track the container width so each page scales to fit the modal.
  useEffect(() => {
    if (!isOpen) return;
    setHasError(false);
    setNumPages(0);

    const el = containerRef.current;
    if (!el) return;

    const measure = () => setPageWidth(el.clientWidth);
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOpen]);

  if (!isOpen) return null;

  const openInNewTab = () =>
    window.open(PM_CHECKLIST_PDF_URL, '_blank', 'noopener,noreferrer');

  const handleDownload = () => {
    try {
      const link = document.createElement('a');
      link.href = PM_CHECKLIST_PDF_URL;
      link.download = PM_CHECKLIST_DOWNLOAD_FILENAME;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      openInNewTab();
    }
  };

  // Only pass an explicit width once the container is wider than the gutter;
  // a container narrower than HORIZONTAL_GUTTER would otherwise yield a zero or
  // negative width, which pdf.js turns into a blank/errored page canvas.
  const renderedPageWidth =
    pageWidth > HORIZONTAL_GUTTER
      ? Math.min(pageWidth - HORIZONTAL_GUTTER, MAX_PAGE_WIDTH)
      : undefined;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4">
      <div className="flex h-[95vh] w-full max-w-[1280px] flex-col overflow-hidden rounded-[4px] bg-white">
        <div className="flex h-[56px] min-h-[56px] w-full flex-row items-center justify-between border-b border-bordergray p-4 align-middle">
          <h2 className="text-[16px] font-[600] text-primary">
            PM Inspection Checklist
          </h2>
          <XIcon
            className="hover:cursor-pointer hover:opacity-80"
            onClick={onClose}
            weight="regular"
            size={24}
          />
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-hidden p-6">
          <p className="text-sm text-graysecondary">
            Preview the blank Preventive Maintenance inspection form for your
            trucks. Download a copy to print or fill out.
          </p>

          <div
            ref={containerRef}
            data-testid="pm-checklist-viewer"
            className="min-h-0 w-full flex-1 overflow-auto rounded-lg border border-bordergray bg-gray-50"
          >
            {hasError ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
                <p className="text-sm text-graysecondary">
                  We couldn&apos;t display the preview here.
                </p>
                <button
                  type="button"
                  onClick={openInNewTab}
                  className="text-sm font-semibold text-ctadarkgreen underline hover:opacity-80"
                >
                  Open the PDF in a new tab
                </button>
              </div>
            ) : (
              <Document
                file={PM_CHECKLIST_PDF_URL}
                onLoadSuccess={({ numPages: loaded }) => setNumPages(loaded)}
                onLoadError={() => setHasError(true)}
                onSourceError={() => setHasError(true)}
                loading={
                  <div className="flex h-full items-center justify-center p-6 text-sm text-graysecondary">
                    Loading preview…
                  </div>
                }
                className="flex flex-col items-center gap-4 py-4"
              >
                {Array.from({ length: numPages }, (_, index) => (
                  <Page
                    key={`pm-page-${index + 1}`}
                    pageNumber={index + 1}
                    width={renderedPageWidth}
                    className="shadow-sm"
                  />
                ))}
              </Document>
            )}
          </div>

          <div className="flex justify-end">
            <GreenGradientButton onClick={handleDownload}>
              <DownloadSimpleIcon size={20} weight="regular" />
              Download PDF
            </GreenGradientButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PMChecklistModal;
